import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Package, RotateCcw, ChevronDown, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// The single source of truth for the account menu items, used by both public
// headers (storefront + marketplace home) so the menu is identical everywhere.
export function buildAccountItems(role, summary = {}) {
  if (role === "travel_agent") return [{ key: "dashboard", to: "/agent/home", label: "Seller Dashboard", icon: LayoutDashboard }];
  if (role === "super_admin") return [{ key: "dashboard", to: "/admin", label: "Admin Dashboard", icon: LayoutDashboard }];
  if (role === "support_agent") return [{ key: "dashboard", to: "/support-console", label: "Support Console", icon: LayoutDashboard }];
  return [
    { key: "profile", to: "/app/profile", label: "Profile", icon: User },
    { key: "dashboard", to: "/app/home", label: "Dashboard", icon: LayoutDashboard },
    { key: "orders", to: "/app/shop-orders", label: "Orders", icon: Package, badge: summary.open_orders },
    { key: "returns", to: "/app/returns", label: "Returns", icon: RotateCcw, badge: summary.pending_returns },
  ];
}

// variant: "dark" (on the dark storefront header) | "light" (on the white marketplace-home header)
export default function AccountMenu({ variant = "dark" }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ open_orders: 0, pending_returns: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (!user || user.role !== "customer") return;
    const refetch = () => api.get("/marketplace/account/summary").then((r) => setSummary(r.data)).catch(() => {});
    refetch();
    window.addEventListener("account-updated", refetch);
    window.addEventListener("focus", refetch);
    return () => { window.removeEventListener("account-updated", refetch); window.removeEventListener("focus", refetch); };
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;
  const items = buildAccountItems(user.role, summary);
  const triggerCls = variant === "light"
    ? "inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-blue-400"
    : "inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-xs font-medium text-white hover:bg-white/20";
  const chevCls = variant === "light" ? "h-3.5 w-3.5 text-slate-400" : "h-3.5 w-3.5 text-white/60";

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="shop-header-account" className={triggerCls}>
        <User className="h-4 w-4" /> <span className="hidden max-w-[90px] truncate sm:inline">{(user.name || "Account").split(" ")[0]}</span> <ChevronDown className={chevCls} />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 text-slate-700 shadow-xl" data-testid="account-menu">
          {items.map((m) => (
            <button key={m.key} onClick={() => { setOpen(false); nav(m.to); }} data-testid={`account-menu-${m.key}`} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-slate-50">
              <m.icon className="h-4 w-4 text-slate-400" /> <span className="flex-1 text-left">{m.label}</span>
              {m.badge > 0 && <span data-testid={`account-menu-badge-${m.key}`} className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-100 px-1.5 text-[11px] font-bold text-blue-700">{m.badge}</span>}
            </button>
          ))}
          <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} data-testid="account-menu-logout" className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
